import { defineStore } from 'pinia'
import { ref } from 'vue'
import {ChecksService} from '@/services/ChecksService'
import { clearDatabase, type CheckOfflineMetadata } from '@/offlineDatabase'
import type { TodaysCheck } from '@/interfaces/TodaysCheck'
import { CheckOfflineService } from '@/services/CheckOfflineService'
import deviceApi from '@/deviceApi'
import { CheckService, type CalendarCheckDto, type GetApiCheckCalendarResponse } from '@/client'

export const useChecksStore = defineStore('checksStore', () => {
  const checks = ref<TodaysCheck[]>();
  const checksInProgress = ref<CheckOfflineMetadata[]>()
  const checksService = new ChecksService();
  const progressService = CheckOfflineService();
  const currentCheckMetadata = ref<CheckOfflineMetadata | undefined>(undefined)
  const calendar = ref([] as CalendarCheckDto[]);

  const lastSync = (d: Date | undefined = undefined): Date | undefined => {
    if (d != undefined) localStorage.setItem("lastSync", d.toISOString());
    return localStorage.getItem("lastSync") ? new Date(localStorage.getItem("lastSync")!) : undefined;
  }

  const getChecks = async (timecheck:boolean = true) => {
    if (lastSync() == undefined || lastSync()!.getDate() != new Date().getDate())
      await clearDatabase();

    if (timecheck) {
      if (lastSync() !== undefined && checks.value !== undefined) {
        const currentDate = new Date()
        const diff = Math.abs(currentDate.getTime() - lastSync()!.getTime())
        const diffMinutes = (diff / (1000 * 60))
        if (diffMinutes < 2) return false;
      }
    }

    checks.value = await checksService.getTodaysChecks();
    checks.value.forEach((check: TodaysCheck) => {
      setAlert(check);
    });
    lastSync(new Date());
  }

  const getChecksInProgress = async () => {
    checksInProgress.value = await progressService.getAllChecks()
  }

  const setCurrentCheck = async (check: CheckOfflineMetadata) => {
    currentCheckMetadata.value = check;
  };

  const isOverdue = (check: TodaysCheck): boolean => {
    if (check.isCompleted) return false;
    const currentDate = getDueAsDate(check)
    return currentDate < new Date()
  }

  const getDueAsDate = (check: TodaysCheck): Date => {
    const currentDate = new Date()

    const timeParts = check.due.split(':')
    const hours = parseInt(timeParts[0])
    const minutes = parseInt(timeParts[1])

    if (hours == 0 && minutes == 0) {
      return currentDate;
    }

    currentDate.setHours(hours)
    currentDate.setMinutes(minutes)
    currentDate.setSeconds(0)
    currentDate.setMilliseconds(0)
    return currentDate;
  }

  const setAlert = async (check: TodaysCheck) => {
    if (check.due != "" && check.category == "Today" && !isOverdue(check) && !check.isCompleted) {
      deviceApi.postMessage({
        type: "alert",
        minutes: 0,
        start: new Date(),
        message: check.title,
        checkId: check.id,
        due: getDueAsDate(check)
      })
    }
  }

  async function getCheckCalendar(start: string, end: string) {
    return await CheckService.getApiCheckCalendar({
      from: start,
      to: end,
    })
      .then((response: GetApiCheckCalendarResponse) => {
        calendar.value = response;
        return Promise.resolve(response);
      })
      .catch((error: any) => {
        return Promise.reject(error);
      });
  }

  return {
    getChecks,
    isOverdue,
    getDueAsDate,
    getChecksInProgress,
    setCurrentCheck,
    getCheckCalendar,
    checks,
    checksInProgress,
    currentCheckMetadata,
    calendar
  }
})


